import { useMemo } from 'react';

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  stroke?: string;
  fill?: string;
  strokeWidth?: number;
  className?: string;
  preserveAspectRatio?: string;
}

export function Sparkline({
  data,
  width = 100,
  height = 32,
  stroke = 'currentColor',
  fill,
  strokeWidth = 1.5,
  className,
  preserveAspectRatio = 'none',
}: SparklineProps) {
  const { path, areaPath } = useMemo(() => {
    if (data.length === 0) return { path: '', areaPath: '' };
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const step = data.length > 1 ? width / (data.length - 1) : 0;

    const points = data.map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * height;
      return [x, y] as const;
    });

    const path = points
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`)
      .join(' ');
    const areaPath = `${path} L${width},${height} L0,${height} Z`;
    return { path, areaPath };
  }, [data, width, height]);

  if (!path) return null;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height="100%"
      preserveAspectRatio={preserveAspectRatio}
      className={className}
    >
      {fill && <path d={areaPath} fill={fill} />}
      <path d={path} fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin="round" />
    </svg>
  );
}
